export default class GrmMediaStreamTrackGenerator {
    id;
    _options;
    _props = {};
    _eventListeners = [];

    static isSupported () {
        return !!globalThis.MediaStreamTrackProcessor
                && !!globalThis.MediaStreamTrackGenerator;
    }

    constructor(options) {
        // super();
        this.id = globalThis.crypto ? globalThis.crypto.randomUUID() : 'grmTrackGen-'+Date.now();
        this._options = Object.assign({}, options || {});
    }

    async init () {
        let {stream, track, fps} = this._options;
        if(stream){
            track = stream.getVideoTracks()[0];
        }else if(track){
            stream = new MediaStream([track]);
        }

        const canvas = new OffscreenCanvas(1, 1);
        // const canvas = document.createElement('canvas');
        const canvasCtx = canvas.getContext('2d', {
            alpha: false,
            willReadFrequently: false
            // desynchronized: true,  // 레이턴시 감소
        });
        Object.assign(canvasCtx, {
            imageSmoothingEnabled: false,  // 기본 스무딩 비활성화
        })
        // const canvasCtx = canvas.getContext('webgl');

        Object.assign(this._props, {
            stream,
            track,
            canvas,
            canvasCtx
        });

        const {width, height, frameRate, facingMode} = track.getSettings()
        this.resizeCanvas(width, height);
        this._props.fps = fps || frameRate || 30;
        this._props.facingMode = facingMode;
    }

    async destroy () {
        this.destroyed = true;
        this.pause();

        this._props.writer?.closed.catch(e => {
            this._props.writer.close();
        })

        let {
            processor, reader,
            generator, writer
        } = this._props;

        if(processor){
            reader = reader || processor.readable?.getReader();
            if(reader){
                try{
                    await reader.cancel();
                }catch(e){}
                try{
                    await reader.releaseLock();
                }catch(e){}
            }
        }
        if(generator){
            try{
              writer = writer || generator.writable?.getWriter();
              if(writer){
                await writer.close();
              }
            }catch(e){}
            try{
                generator.stop?.();
            }catch(e){}
        }

        this._eventListeners = null;
    }

    getRenderCanvas () {
        return this._props.canvas;
    }

    addVideoFrameEventListener (listener, options={}) {
        const list = this._eventListeners;
        let exist = list.find(ls => ls.listener === listener);
        if(exist) return this;
        list.push({
            listener,
            options: {...options}
        });

        if(options.order){
            list.sort((a, b) => {
                if(a.order === undefined){
                    return 2;
                }else if(b.order === undefined){
                    return -2;
                }else if(a.order === undefined && b.order === undefined){
                    return 0;
                }
                return a.order - b.order;
            });
        }else{
            delete options.order;
        }
    }
    removeVideoFrameEventListener (listener) {
        let idx = this._eventListeners.findIndex(ls => ls.listener === listener);
        if(idx > -1){
            this._eventListeners.splice(idx, 1);
        }
        return this;
    }
    async _triggerVideoFrameEvent ({image, canvas}) {
        // event trigger
        const listeners = this._eventListeners;
        if(listeners && listeners.length === 0){
            return;
        }

        const event = {
            type: 'videoFrame',
            image,
            canvas,
        };
        queueMicrotask(() => {
            try{
                let gc;
                listeners.forEach(el => {
                    if(el.removed || el.options.once === true){
                        gc = gc || [];
                        gc.push(el);
                    }
                    if(el.removed) return;

                    el.listener(
                        Object.assign(event, {
                            removeListener: () => {
                                el.removed = true;
                            }
                        })
                    );
                });
                gc && gc.forEach(el => {
                    let idx = listeners.findIndex(el);
                    if(idx === -1) return;
                    listeners.splice(idx, 1);
                });
            }catch(e){
                console.error(e);
            }
        });
    }

    _getMediaStreamTrackProcessor () {
        let {
            readableStream,
            writableStream
        } = this._props;
        if(readableStream){
            return {
                readableStream,
                writableStream
            };
        }

        /////
        const {track} = this._props;
        if(!track) return {};
        let processor = new MediaStreamTrackProcessor({track});
        let generator = new MediaStreamTrackGenerator({kind: 'video'});
        generator._grm_tg = this;

        readableStream = processor.readable;
        writableStream = generator.writable;
        Object.assign(this._props, {
            processor,
            generator,
            readableStream,
            writableStream
        });
        return {
            readableStream,
            writableStream
        };
    }

    async requestVideoFrameRender (fps) {
        const {readableStream, writableStream} = this._getMediaStreamTrackProcessor();
        if(readableStream.locked){
            return;
        }

        const transformer = new TransformStream({
            transform: async (videoFrame, controller) => {
                try {
                    if(this.destroyed){
                        videoFrame.close();
                        controller.terminate();
                        return;
                    }

                    const srcTrackFrame = (() => {
                        const track = this.getSourceVideoMediaStreamTrack();
                        if(!track) return 30;
                        let frame = this.getSourceVideoMediaStreamTrack().getSettings().frameRate;
                        return frame;
                    })();
                    let frame = fps || this._props.fps || 30;
                    frame = Math.min(frame, srcTrackFrame);
                    let term = 1000 / frame / transformer.frameGap;

                    const gapMs = (videoFrame.timestamp - transformer._last) / 1000;
                    if(gapMs >= 0 && gapMs < term){
                        videoFrame.close();
                        const dstTrack = this.getVideoMediaStreamTrack();
                        if(dstTrack){
                            const dstTrackFrame = dstTrack.getSettings().frameRate;
                            if(dstTrackFrame && dstTrackFrame < frame-5){
                                transformer.frameGap = Math.round(frame / dstTrackFrame);
                                // console.log('@@@@@ set frame gap:', transformer.frameGap);
                            }
                        }
                        return;
                    }
                    transformer._last = videoFrame.timestamp;

                    let videoWidth = videoFrame.displayWidth;
                    let videoHeight = videoFrame.displayHeight;
                    let {canvas, ctx} = this._props;
                    if(!ctx){
                        ctx = this._props.ctx = canvas.getContext('2d');
                    }
                    let {width, height} = canvas;

                    let sX=0, sY=0, ratio=1;
                    let dX=0, dY=0;

                    const resizeRequired = width !== videoWidth || height !== videoHeight;
                    if(resizeRequired){
                        const {resizeMode} = this._props;
                        switch(resizeMode){
                            case 'crop': {
                                if(videoWidth > width || videoHeight > height){
                                    let wRatio = width / videoWidth;
                                    let hRatio = height / videoHeight;
                                    ratio = Math.max(wRatio, hRatio);
                                    sX = ((videoWidth * ratio) - width) / 2 / ratio;
                                    sY = ((videoHeight * ratio) - height) / 2 / ratio;
                                    videoWidth = width / ratio;
                                    videoHeight = height / ratio;
                                    break;
                                }
                            }
                            case 'res':
                            default:
                                let wRatio = width / videoWidth;
                                let hRatio = height / videoHeight;
                                ratio = Math.min(wRatio, hRatio)
                                width = videoWidth * ratio;
                                height = videoHeight * ratio;
                                dX = (canvas.width - width) / 2;
                                dY = (canvas.height - height) / 2;
                                break;
                        }
                    }

                    /**
                     * videoFrame을 바로 그릴 경우 녹색 화면이 깜빡이는 케이스 발생하여 임시 사용 중단.
                     */
                    // let frameSrc = videoFrame;
                    // const drawBlur = blurSize > 0;
                    // if(drawBlur){
                    //     const videoFrameRatio = videoWidth / videoHeight;
                    //     if(canvas._ratio !== videoFrameRatio){
                    //         // 비율이 달라지면 이전 잔상이 남아 clear 해준다.
                    //         ctx.clearRect(0,0, width, height);
                    //     }
                    //     canvas._ratio = videoFrameRatio;
                    //
                    //     ctx.drawImage(videoFrame,
                    //         sX, sY, videoWidth, videoHeight,
                    //         dX, dY, width, height
                    //     );
                    //     videoFrame.close();
                    //     frameSrc = ctx.canvas;
                    // }

                    let frameSrc = await (async () => {
                        let result = await this._render({
                            image: videoFrame,
                            canvas, ctx,
                            sX, sY, videoWidth, videoHeight,
                            dX, dY, width, height
                        })
                        return result || ctx.canvas;
                    })();

                    // if(!resizeRequired && frameSrc === videoFrame){
                    //     // 편집이 없으면 원본.
                    //     controller.enqueue(frameSrc);
                    //     return;
                    // }

                    if(this.paused()){
                        this._drawPauseImage();
                    }

                    const newVideoFrame = new VideoFrame(frameSrc, {
                        timestamp: videoFrame.timestamp,
                        // visibleRect: {
                        //     x:sX, y:sY,
                        //     width, height
                        // }
                    });
                    controller.enqueue(newVideoFrame);
                    videoFrame.close();

                    // event trigger
                    // this._triggerVideoFrameEvent({canvas})
                } catch (e) {
                    console.error('Error processing video frame:', e);
                    videoFrame.close();
                } finally {
                }
            }
        });
        Object.assign(transformer, {
            _last: 0,
            frameGap: 2,
        })

        if(this._props.pipeAbortController){
            try {
                await this._props.pipeAbortController.abort();
            } catch (e) {
                console.warn('Previous pipe abort failed:', e);
            }
            this._props.pipeAbortController = null;
        }

        this._props.pipeAbortController = new AbortController();
        this._props.pipe = readableStream
            .pipeThrough(transformer)
            .pipeTo(writableStream, {
                preventClose: true,
                signal: this._props.pipeAbortController.signal
            })
            .catch(error => console.error('Stream processing error:', error));
    }

    async _render ({
       image, canvas, ctx,
       sX, sY, videoWidth, videoHeight,
       dX, dY, width, height
    }) {
        const {mirror} = this._props;
        try{
            if(mirror){
                ctx.save();
                ctx.scale(-1, 1);
                ctx.translate(-ctx.canvas.width, 0);
            }
            ctx.drawImage(image,
                sX, sY, videoWidth, videoHeight,
                dX, dY, width, height
            );
        }finally{
            if(mirror){
                ctx.restore();
            }
        }
        return ctx.canvas;
    }

    setBlur (blurSize) {
        const set = blurSize !== false && blurSize > 0;

        const {canvasCtx} = this._props;
        if(set){
            this._props.blurSize = blurSize;
            canvasCtx.filter = `blur(${blurSize}px)`;
        }else{
            this._props.blurSize = 0;
            canvasCtx.filter = `none`;
        }
    }
    setMirror (mirror) {
        if(mirror === undefined){
            mirror = true;
        }
        this._props.mirror = mirror !== false;
    }

    changeFPS (fps) {
        if(!isNaN(fps)){
            this._props.fps = parseFloat(fps);
        }
        return this._props.fps;
    }

    paused () {
        return this._props.paused === true;
    }
    pause (options) {
        this._props.paused = true;
        if(options?.pauseText !== undefined){
            this._props.pauseText = options?.pauseText;
        }
        this._drawPauseImage();
    }
    _drawPauseImage (ctx) {
        if(!this.paused()) return;

        if(!ctx){
            ctx = this._props.ctx;
            if(!ctx){
                ctx = this._props.ctx = this._props.canvas.getContext('2d');
            }
        }
        let canvas = ctx.canvas;
        let {pauseImage, pauseText} = this._props;
        pauseText = pauseText || '● ● ●';
        if(!pauseImage
            || pauseText !== pauseImage.pauseText
            || this.destroyed
        ){
            const ocs = new OffscreenCanvas(canvas.width, canvas.height);
            const ocsCtx = ocs.getContext('2d');
            ocsCtx.fillStyle = '#000';
            ocsCtx.fillRect(0, 0, ocs.width, ocs.height);
            const fontSize = canvas.height * 0.08;
            Object.assign(ocsCtx, {
                font: `${fontSize}px Arial`,
                textAlign: 'center',
                textBaseline: 'middle',
                fillStyle: '#bdbdbd'
            });
            if(!this.destroyed){
                ocsCtx.fillText(pauseText, ocs.width / 2, ocs.height / 2);
            }
            pauseImage = this._props.pauseImage = ocs;
            pauseImage.pauseText = pauseText;
        }
        if(ctx){
            ctx.drawImage(pauseImage,
                0, 0, canvas.width, canvas.height
            );
        }
    }

    resume () {
        this._props.paused = false;
        this.requestVideoFrameRender();
    }

    resize (width, height, resizeMode) {
        this.resizeCanvas(width, height);
        this._props.resizeMode = resizeMode;
    }

    resizeCanvas (width, height) {
        if(typeof width === 'object'){
            let o = width;
            if(o.width && o.height){
                width = o.width;
                height = o.height;
            }else{
                function parse(size){
                    return size.ideal || size.max || size.min;
                }
                width = parse(width);
                height = parse(height);
            }
        }
        Object.assign(this._props.canvas, {width, height});

        // // 캔버스 크기 설정
        // const {canvas, canvasCtx} = this._props;
        // const dpr = globalThis.devicePixelRatio || 1;
        // Object.assign(canvas, {
        //     width: width * dpr,
        //     height: height * dpr
        // });
        // if(canvas.style){
        //     Object.assign(canvas.style, {
        //         width: width + 'px',
        //         height: height + 'px'
        //     });
        // }
        // // DPR 적용
        // canvasCtx.scale(dpr, dpr);
    }

    async toBlob (type, quality, callback) {
        return new Promise((resolve, reject) => {
            switch(type && type.toLowerCase() || ''){
                case 'jpg':
                case 'jpeg':
                    type = 'jpeg';
                case 'png':
                case 'webp':
                    type = 'image/'+type;
                    break;
            }

            const canvas = this._props.canvas;
            try{
                canvas.toBlob(result => {
                    if(callback){
                        callback(result);
                    }
                    resolve(result);
                }, type, quality);
            }catch(e){
                reject(e);
                if(callback){
                    callback(null, e);
                }
            }
        });
    }

    toDataURL (type, encoderOptions) {
        const canvas = this._props.canvas;
        return canvas.toDataURL(type, encoderOptions);
    }

    getSourceMediaStream () {
        return this._props.stream;
    }

    getSourceVideoMediaStreamTrack () {
        return this._props.track;
    }

    getMediaStream () {
        const track = this._props.generator;
        if(!track) return null;

        let stream = this._props.dstStream;
        if(stream) return stream;

        stream = this._props.dstStream = new MediaStream([track]);

        const canvas = this.getRenderCanvas();
        const oriTrack = this.getSourceVideoMediaStreamTrack();

        // modify oriTrack
        (() => {
            if(oriTrack._grm_vtp) return;
            oriTrack._grm_tg = this;
            oriTrack._grm_vtp = {
                getSenderTrack: () => {
                    return this.getVideoMediaStreamTrack();
                },
                oriApplyConstraints: ((t, func)=>{
                    let options = this._options;
                    return function(){
                        // if(options.maintainRatio === true){
                        //     let {width, height} = t.getSettings();
                        //     Object.assign(arguments[0], {width, height});
                        // }
                        if(options.maintainRatio === true) return Promise.resolve();
                        return func.apply(t, arguments);
                    }
                })(oriTrack, track.applyConstraints),
            };
            oriTrack.applyConstraints = async constraints => {
                // 아래 로직 확인 필요
                // return oriTrack._grm_vtp.oriApplyConstraints(constraints);
                const track = this.getVideoMediaStreamTrack();
                return track.applyConstraints(constraints);
            };
        })();
        // modify capture track
        const modifyCaptureTrack = (track) => {
            track._grm_tg = this;
            track._grm_vtp = {
                getOriTrack: () => {
                    return oriTrack;
                },
                // oriApplyConstraints: track.applyConstraints.bind(track),
                oriApplyConstraints: ((t, func)=>{
                    let options = this._options;
                    return function(){
                        // if(options.maintainRatio === true){
                        //     let {width, height} = t.getSettings();
                        //     Object.assign(arguments[0], {width, height});
                        // }
                        if(options.maintainRatio === true) return Promise.resolve();
                        return func.apply(t, arguments);
                    }
                })(track, track.applyConstraints),
                jobs: []
            }
            const applyConstraints = async constraints => {
                let change = true;
                try{
                    await track._grm_vtp.oriApplyConstraints(constraints);
                    const settings = track.getSettings();
                    for(let key in constraints){
                        if(constraints[key] != settings[key]){
                            change = false;
                            break;
                        }
                    }
                }catch(e){
                    change = false;
                }

                if(constraints.width && constraints.height){
                    this.resize(constraints.width, constraints.height);
                }
                if(!change){
                    if(constraints.width && constraints.height){
                        if(constraints.width > canvas.width || constraints.height > canvas.height){
                            const oriTrack = track._grm_vtp.getOriTrack();
                            const {width, height} = constraints;
                            const cons = oriTrack.getConstraints();
                            Object.assign(cons, {width, height});
                            await oriTrack._grm_vtp.oriApplyConstraints(cons);
                        }
                    }
                    if(constraints.frameRate){
                        this.changeFPS(constraints.frameRate);
                    }
                }
            };
            track.applyConstraints = async constraints => {
                const jobs = track._grm_vtp.jobs;
                if(jobs.runJob){
                    // console.log('@@ track apply cons : running, jobs:', jobs.length+1, Date.now());
                    let job = {};
                    job.p = new Promise((resolve, reject) => {
                        Object.assign(job, {
                            resolve, reject, constraints,
                            created: Date.now()
                        });
                    });
                    jobs.push(job);
                    return job.p;
                }

                jobs.runJob = true;
                // console.log('@@@@@@@ track apply cons : run, jobs:', jobs.length, constraints);

                try{
                    await applyConstraints.call(track, constraints);
                }finally{
                    // console.log('@@ finally:', constraints)
                    jobs.runJob = false;
                    if(track._grm_vtp.jobs.length > 0){
                        const job = track._grm_vtp.jobs.shift();
                        // console.log('@@ track apply cons : run next, jobs:',
                        //     jobs.length+1, job.constraints, (Date.now()-job.created)+'ms', Date.now());
                        track.applyConstraints(job.constraints)
                            .then(job.resolve)
                            .catch(job.reject);
                    }
                }
            };
        }
        modifyCaptureTrack(track);

        return stream;
    }

    getVideoMediaStreamTrack () {
        const stream = this.getMediaStream();
        return stream?.getVideoTracks()[0];
    }

    async applyVideoMediaStreamTrackConstraints (constraints) {
        const track = this.getVideoMediaStreamTrack();
        const cons = track.getConstraints();
        Object.assign(cons, constraints);
        return track.applyConstraints(cons);
    }

    async replaceVideoMediaStreamTrack (track, requestVideoFrameRender) {
        (() => {
            const stream = this.getSourceMediaStream();
            if(!stream) return;
            const old = stream.getVideoTracks()[0];
            if(old === track){
                return;
            }
            if(old){
                stream.removeTrack(old);
            }
            if(track){
                stream.addTrack(track);
            }
        })();

        try{
            await (async () => {
                let {
                    processor, reader, readableStream
                } = this._props;
                if(!processor) return;
                reader = reader || processor.readable?.getReader();
                if(reader){
                    try{
                        await reader.cancel();
                    }catch(e){}
                    try{
                        await reader.releaseLock();
                    }catch(e){}
                }
            })();
        }catch(e){
            console.warn(e);
        }

        this._props.track = track;
        let processor = new MediaStreamTrackProcessor({track});
        let readableStream = processor.readable;
        Object.assign(this._props, {
            processor,
            readableStream,
        });

        if(requestVideoFrameRender !== false){
            this.requestVideoFrameRender();
        }
    }
}
